import { useState } from 'react';
import { formatPhone } from '../utils/PhoneUtils';

/**
 * Formats a phone number with parentheses and hyphens as the user types
 * e.g., (123) 456-7890
 * @param input Raw phone number digits
 * @returns Formatted phone number string for display
 */
const formatPhoneForDisplay = (input: string): string => {
  // Remove any non-digit characters
  const digits = input.replace(/\D/g, '');

  if (digits.length === 0) {
    return '';
  }
  if (digits.length <= 3) {
    return `(${digits}`;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

/**
 * Gets the raw digits from a formatted phone number
 * @param formattedPhone Formatted phone number string
 * @returns Raw digits
 */
const getRawDigits = (formattedPhone: string): string => formattedPhone.replace(/\D/g, '');

/**
 * Custom hook for handling phone input with real-time formatting.
 * Formats the phone for display with parentheses and hyphens as the user types.
 * @param initialValue Initial phone number value
 * @returns Tuple containing [displayPhone, onPhoneChange, rawPhone]
 * - displayPhone: Formatted phone number for display (e.g., (123) 456-7890)
 * - onPhoneChange: Event handler for input changes
 * - rawPhone: The phone number in the format expected by the backend
 */
export const useFormattedPhoneInput = (initialValue = '') => {
  // Format initial value if provided
  const initialFormatted = initialValue ? formatPhoneForDisplay(initialValue) : '';
  const [displayPhone, setDisplayPhone] = useState(initialFormatted);

  const onPhoneChange = (e: React.FormEvent<HTMLInputElement>) => {
    // Get the current cursor position before we update the value
    const cursorPosition = e.currentTarget.selectionStart || 0;

    // Get raw value and current display value
    const rawValue = e.currentTarget.value.replace(/\D/g, '');
    const currentDisplayValue = displayPhone;

    if (rawValue.length > 0) {
      // Format for display with parentheses and hyphens
      const formattedDisplay = formatPhoneForDisplay(rawValue);
      setDisplayPhone(formattedDisplay);

      // Adjust cursor position for added formatting characters
      // Calculate how many formatting characters we've passed
      setTimeout(() => {
        if (e.currentTarget) {
          // We need to calculate the new cursor position based on how many formatting chars were added/removed
          // This is a simplification and might need refinement for perfect cursor positioning
          const previousRawLength = getRawDigits(currentDisplayValue.substring(0, cursorPosition)).length;
          const newRawAtCursor = getRawDigits(e.currentTarget.value).substring(0, previousRawLength);

          let newPosition = formattedDisplay.indexOf(newRawAtCursor.charAt(newRawAtCursor.length - 1));
          if (newPosition >= 0) newPosition += 1;
          else newPosition = cursorPosition;

          e.currentTarget.setSelectionRange(newPosition, newPosition);
        }
      }, 0);
    } else {
      setDisplayPhone('');
    }
  };

  // Return the formatted display value for the input
  // and compute the raw value for backend submission
  // Ensure rawPhone only contains valid phone number digits
  const digits = displayPhone ? getRawDigits(displayPhone) : '';
  // Use formatPhone from PhoneUtils but ensure we only get numeric characters
  // If formatPhone returns a non-numeric value, fall back to just the digits
  const formattedResult = digits ? formatPhone(digits) : '';
  const rawPhone = formattedResult ? formattedResult.replace(/[^0-9+]/g, '') : '';

  return [displayPhone, onPhoneChange, rawPhone] as const;
};
